import { Box, Skeleton } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";

/**
 * Loading layout for the week, shaped like the real rows so the page does
 * not jump when the plan arrives: the first row expanded (date, kcal, the
 * two action buttons, a meal header, then three cards with a photo block and
 * text lines), the next two collapsed (a tab strip and three cards).
 */
const PlanSkeleton = () => {
  const theme: Theme = useTheme();

  const block = (sx: object) => (
    <Skeleton variant="rounded" animation="pulse" sx={{ ...styles.block(theme), ...sx }} />
  );

  const card = (key: number) => (
    <Box key={key} sx={styles.card(theme)}>
      {block({ width: "100%", height: "150px", borderRadius: 0 })}
      <Box sx={styles.cardBody}>
        {block({ width: "75%", height: "16px" })}
        <Box sx={styles.inline}>
          {block({ width: "64px", height: "20px", borderRadius: "12px" })}
          {block({ width: "45%", height: "14px" })}
        </Box>
        {block({ width: "40%", height: "14px" })}
        <Box sx={styles.inline}>
          {block({ width: "72px", height: "20px", borderRadius: "12px" })}
          {block({ width: "88px", height: "20px", borderRadius: "12px" })}
          {block({ width: "60px", height: "20px", borderRadius: "12px" })}
        </Box>
      </Box>
    </Box>
  );

  const header = (
    <Box sx={styles.header}>
      <Box sx={styles.inline}>
        {block({ width: "128px", height: "16px" })}
        {block({ width: "56px", height: "14px" })}
      </Box>
      <Box sx={styles.inline}>
        {block({ width: "38px", height: "38px", borderRadius: "8px" })}
        {block({ width: "38px", height: "38px", borderRadius: "8px" })}
      </Box>
    </Box>
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.row(theme)}>
        {header}
        <Box sx={styles.inline}>
          {block({ width: "72px", height: "16px" })}
          {block({ width: "40px", height: "14px" })}
        </Box>
        <Box sx={styles.cards}>{[0, 1, 2].map(card)}</Box>
      </Box>
      {[0, 1].map((row) => (
        <Box key={row} sx={styles.row(theme)}>
          {header}
          <Box sx={styles.inline}>
            {block({ width: "104px", height: "14px" })}
            {block({ width: "120px", height: "14px" })}
            {block({ width: "88px", height: "14px" })}
            {block({ width: "128px", height: "14px" })}
          </Box>
          <Box sx={styles.cards}>{[0, 1, 2].map(card)}</Box>
        </Box>
      ))}
    </Box>
  );
};

export default PlanSkeleton;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    // Mirror WeekPlanView's ScrollableWrapper padding + gutter and the page's
    // -16px pull, so the rows sit where the real ones will.
    paddingRight: "16px",
  },
  block: (theme: Theme) => ({
    bgcolor: theme.colorTokens.N[30],
    flexShrink: 0,
  }),
  // Same shell as WeekDayRow.
  row: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  inline: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
  },
  // Same shell as MealItemCard.
  card: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    overflow: "hidden",
    borderRadius: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  cardBody: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "10px",
    padding: "16px",
  },
};
